import React from "react";
import { formatPrintDate, formatSite, MetadataGrid, PrintFooter, PrintHeader, SectionBar } from "./PrintRecordParts";

const LABEL_TO_KEY = {
  "Fluid Leaks": "fluid_leaks",
  "Carriage Rollers / Slide Blocks": "carriage_rollers_slide_blocks",
  "Mast – Chains / Retainers / Sheaves / Anchors": "mast_chains_retainers_sheaves_anchors",
  "Forks and Fork Lock Pins": "forks_and_fork_lock_pins",
  "Hydraulic Cylinders": "hydraulic_cylinders",
  "Brake Master Cylinder": "brake_master_cylinder",
  "Over Mast Hoses and Cables": "over_mast_hoses_and_cables",
  "Caster Assembly": "caster_assembly",
  "Drive Unit / Radial Rings": "drive_unit_radial_rings",
  "Hydraulic Manifold / Piping": "hydraulic_manifold_piping",
  "Tractor Frame / Base Legs / Doors & Covers": "tractor_frame_base_legs_doors_and_covers",
  "Lift Cyl.": "lift_cyl",
  "Reach Cyl.": "reach_cyl",
  "Tilt Cyl.": "tilt_cyl",
  "Sideshift Cyl.": "sideshift_cyl",
  Connections: "connections",
  "Relief Pressures": "relief_pressures",
  Lift: "lift",
  Aux: "aux",
  Steer: "steer",
  "Control Wiring": "control_wiring",
  Switches: "switches",
  Contactors: "contactors",
  "Power Cables": "power_cables",
  Fuses: "fuses",
  Connectors: "connectors",
  "System Short to Frame": "system_short_to_frame",
  Brushes: "brushes",
  Comm: "comm",
  Amps: "amps",
  Ohms: "ohms",
  "Volts Loaded": "volts_loaded",
  "Volts No Load": "volts_no_load",
  "Pos. Leakage": "pos_leakage",
  "Neg. Leakage": "neg_leakage",
  "S.G.": "sg",
  "Electrolyte level": "electrolyte_level",
  "Braking Distance": "braking_distance",
  "Plugging Distance": "plugging_distance",
  "Travel System": "travel_system",
  "Lift / Lower System": "lift_lower_system",
  "Auxiliary System": "auxiliary_system",
  "Steering System": "steering_system",
  "Mini Mast Functions": "mini_mast_functions",
  "Wire Guidance": "wire_guidance",
  Horn: "horn",
  "Travel Alarm": "travel_alarm",
  "Lights – Warning / Brake": "lights_warning_brake",
  "Flood Lights": "flood_lights",
  "Emergency Disconnect": "emergency_disconnect",
  "Static Straps": "static_straps",
  "Mast – Stops / Attach Bolts": "mast_stops_attach_bolts",
  "Load Backrest": "load_backrest",
  "Fork Condition": "fork_condition",
  "Lift Chains": "lift_chains",
  "Brake Pads / Shoes": "brake_pads_shoes",
  "Brake Disk / Drums": "brake_disk_drums",
  "Parking Brake": "parking_brake",
  "Warning Decals": "warning_decals",
  "Data Plate": "data_plate",
  "Tire Condition": "tire_condition",
  "Overhead Guard": "overhead_guard",
  "Safety Belt / Tether": "safety_belt_tether",
  "Safety Glass": "safety_glass",
  "Hydraulic Fluid": "hydraulic_fluid",
  "Oil Filter": "oil_filter",
  "Drive Unit / Gear Oil": "drive_unit_gear_oil",
  "Brake Fluid Reservoir": "brake_fluid_reservoir",
  "Steer Linkage": "steer_linkage",
  "Mast / Chains / Bearings": "mast_chains_bearings",
  "Scissor Assembly": "scissor_assembly",
  "Carriage / Attachments": "carriage_attachments",
  "Load Wheels": "load_wheels",
  "Caster Unit": "caster_unit",
  "Battery Rollers": "battery_rollers",
  "Pump Couplings": "pump_couplings"
};

const PRINT_RESULTS = { P: "PASS", F: "FAIL", N: "N/A" };

const PFNRow = ({ label, selected, number }) => {
  const result = PRINT_RESULTS[selected] || "";
  const modifier = selected
    ? ` print-result-${selected === "N" ? "na" : selected.toLowerCase()}`
    : "";

  return (
    <div className="checklist-print-row pmf-row" data-item-number={number}>
      <span className="print-item-number">{number}</span>
      <div className="checklist-print-item pmf-row-label">{label}</div>
      <span className={`checklist-print-result print-result${modifier}`}>
        {result}
      </span>
    </div>
  );
};

const itemLabel = (item) =>
  typeof item === "string" ? item : item.label || item.name || "";

const itemKeyWithSection = (sectionTitle, item) => {
  const base =
    typeof item === "string"
      ? LABEL_TO_KEY[item] || ""
      : item.name
      ? item.name
      : item.label
      ? LABEL_TO_KEY[item.label] || ""
      : "";
  if (!base) return "";
  if (
    sectionTitle.includes("Motors — Drive") &&
    /^(brushes|comm|amps|ohms)$/.test(base)
  )
    return `drive_${base}`;
  if (
    sectionTitle.includes("Motors — Lift") &&
    /^(brushes|comm|amps|ohms)$/.test(base)
  )
    return `lift_${base}`;
  if (
    sectionTitle.includes("Motors — Aux") &&
    /^(brushes|comm|amps|ohms)$/.test(base)
  )
    return `aux_${base}`;
  if (
    sectionTitle.includes("Motors — Steer") &&
    /^(brushes|comm|amps|ohms)$/.test(base)
  )
    return `steer_${base}`;
  return base;
};

export default function PrintMaintenanceForm({
  header = {},
  sections = [],
  pfn = {},
  footer = {}
}) {
  const h = {
    customerName: header.customerName || "",
    address: header.address || "",
    city: header.city || "",
    state: header.state || "",
    customerPO: header.customerPO || header.customerPo || header.customer_po || "",
    dateStarted: formatPrintDate(header.dateStarted),
    dateCompleted: formatPrintDate(header.dateCompleted),
    unitId: header.unitId || header.unitID || header.unit_number || "",
    make: header.make || "",
    model: header.model || "",
    serialNumber: header.serialNumber || "",
    hourMeter: header.hourMeter ?? header.hour_meter ?? "",
    performedByName: header.performedByName || header.technicianName || ""
  };

  let itemNumber = 0;
  const motorLabels = ["4A", "4B", "4C", "4D"];
  const prepared = sections.map((sec, i) => ({
    ...sec,
    displayNumber: i >= 3 && i <= 6 ? motorLabels[i - 3] : (i >= 7 ? String(i - 2) : String(i + 1)),
    displayTitle: String(sec.title || "").replace(/^\s*\d+[.)]?\s*/, ""),
    numberedItems: (sec.items || []).map((it) => ({ it, number: ++itemNumber })),
  }));
  const renderSection = (sec) => (
          <section key={sec.displayNumber} className="pmf-section print-checklist-section" data-section={sec.displayNumber}>
            <SectionBar>{sec.displayNumber} · {sec.displayTitle}</SectionBar>
            {sec.numberedItems.map(({ it, number }, idx) => {
              const k = itemKeyWithSection(sec.title, it);
              const lbl = itemLabel(it);
              const selected = k ? pfn?.[k] : undefined;
              return (
                <PFNRow
                  key={`${k || lbl}-${idx}`}
                  label={lbl}
                  selected={selected}
                  number={number}
                />
              );
            })}
          </section>
  );
  const title = "Preventive Maintenance";
  const recordId = header.recordId || "";
  return (
    <div className="pmf-wrap print-record print-only">
      <PrintHeader title={title} recordId={recordId} timestamp={header.submittedAt || header.dateCompleted} />
      <MetadataGrid cells={[
        { label: "Customer", value: h.customerName }, { label: "Site", value: formatSite(h), wide: true },
        { label: "Unit ID", value: h.unitId }, { label: "Hour Meter", value: h.hourMeter },
        { label: "Make / Model", value: [h.make, h.model].filter(Boolean).join(" / ") },
        { label: "Serial Number", value: h.serialNumber }, { label: "Performed By", value: h.performedByName },
        { label: "Customer PO / Dates", value: [h.customerPO, [h.dateStarted, h.dateCompleted].filter(Boolean).join(" → ")].filter(Boolean).join(" · ") },
      ]} />
      <div className="maintenance-section-columns">
        <div className="print-section-column" data-column="left">{prepared.slice(0, 3).map(renderSection)}</div>
        <div className="print-section-column" data-column="middle">{prepared.slice(3, 9).map(renderSection)}</div>
        <div className="print-section-column" data-column="right">{prepared.slice(9, 11).map(renderSection)}</div>
      </div>
      <div className="print-closing-grid"><section className="print-closing-block"><SectionBar>Notes / Comments</SectionBar><div className="print-closing-body">{footer.comments || ""}</div></section><section className="print-closing-block"><SectionBar>Signed By</SectionBar><div className="print-closing-body print-signature">{footer.signature || ""}{h.performedByName && <small>{h.performedByName}</small>}</div></section></div>
      <PrintFooter title={title} />
    </div>
  );
}
